import { type ItemOf } from '@shared/board'
import { clsx } from 'clsx'
import { MessagesSquare } from 'lucide-react'
import { useSyncExternalStore } from 'react'
import { Avatar } from '@/components/Avatar'
import { useI18n } from '@/i18n/i18n'
import { sessionName } from '@/session/describe'
import { DRAG_HANDLE } from '../classes'
import { useBoardCtx } from '../context'

/**
 * A conversation a person placed on the board: its avatar, title and whether
 * it is working. Clicking it opens the conversation (the shell handles clicks).
 */
export function SessionNodeView({
  item,
  selected,
}: {
  item: ItemOf<'session'>
  selected: boolean
}) {
  const { sessions } = useBoardCtx()
  const { t, relativeTime } = useI18n()
  const snap = useSyncExternalStore(sessions.subscribe, sessions.getSnapshot)
  const s = snap.sessions.find((x) => x.id === item.data.sessionId)
  return (
    <div
      data-session-node={item.data.sessionId}
      className={clsx(
        'moot-node ' + DRAG_HANDLE,
        'flex cursor-pointer items-center gap-2.5 rounded-xl border bg-surface px-3 py-2.5 shadow-sm transition-shadow hover:shadow-md',
        selected ? 'border-(--focus) shadow-md' : 'border-line',
      )}
      style={{ width: item.w }}
    >
      <Avatar actor={{ kind: 'session', id: item.data.sessionId }} size={28} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13.5px] font-medium text-ink">
          {s ? sessionName(s, t('session.untitled')) : t('sessionNode.gone')}
        </div>
        <div className="flex items-center gap-1.5 text-[11.5px] text-muted">
          {s?.running ? (
            <>
              <span className="size-1.5 animate-pulse rounded-full bg-ai" />
              {t('sessionNode.working')}
            </>
          ) : s ? (
            t('sessionNode.updated', { when: relativeTime(s.updatedAt) })
          ) : null}
        </div>
      </div>
      <MessagesSquare size={15} className="flex-none text-muted" />
    </div>
  )
}
